/**
 * PCOS Assessment Types
 * TypeScript types for quiz data, scoring, and results
 */

// ============================================================================
// PHENOTYPE & ENDOTYPE ENUMS
// ============================================================================

export type PCOSPhenotype = 'A' | 'B' | 'C' | 'D' | 'uncertain';

export type PCOSEndotype = 
  | 'insulin-resistant'
  | 'post-pill'
  | 'adrenal'
  | 'inflammatory'
  | 'mixed'
  | 'uncertain';

// ============================================================================
// QUIZ SECTION TYPES
// ============================================================================

export interface DemographicsData {
  ageRange: string;
  ethnicity?: string;
  heightCm: number;
  weightKg: number;
  waistCircumferenceCm?: number;
  currentMedications: string[];
  hormonalContraceptives: boolean;
  contraceptiveType?: string;
  recentlyStoppedContraceptive: boolean;
  monthsSinceStoppedContraceptive?: number;
  eatingDisorderHistory: boolean;
  pregnancyStatus: 'not-pregnant' | 'pregnant' | 'breastfeeding' | 'trying-to-conceive';
}

export interface MenstrualData {
  ageAtMenarche: number;
  averageCycleLength: number;
  cycleRegularity: 'regular' | 'irregular' | 'absent' | 'variable';
  cycleVariabilityDays?: number;
  periodDurationDays: number;
  tracksOvulation: boolean;
  ovulationMethod?: string;
  detectsOvulationRegularly?: boolean;
  pregnancyHistory: {
    pregnancies: number;
    miscarriages: number;
    fertilityTreatments: boolean;
    troubleConceiving: boolean;
  };
  hasHadUltrasound: boolean;
  ultrasoundResult?: 'normal' | 'polycystic' | 'multi-follicular' | 'unknown';
  ultrasoundDate?: string;
  amhTested: boolean;
  amhValue?: number;
  amhUnit?: 'ng/mL' | 'pmol/L';
}

export interface HyperandrogenicData {
  hirsutism: {
    present: boolean;
    severity: 'none' | 'mild' | 'moderate' | 'severe';
    locations: string[];
  };
  acne: {
    present: boolean;
    severity: 'none' | 'mild' | 'moderate' | 'severe';
    locations: string[];
    persistent: boolean;
  };
  hairThinning: {
    present: boolean;
    pattern: 'none' | 'diffuse' | 'frontal' | 'crown' | 'temples';
  };
  voiceChanges: boolean;
  clitoromegaly: boolean;
  labResults: {
    totalTestosterone?: number;
    freeTestosterone?: number;
    dheaS?: number;
    androstenedione?: number;
    hasElevatedAndrogens: boolean | 'unknown';
  };
}

export interface MetabolicData {
  weightDifficulty: 'none' | 'mild' | 'moderate' | 'severe';
  centralObesity: boolean;
  waistHipRatio?: number;
  fastingGlucose?: number;
  fastingInsulin?: number;
  homaIr?: number;
  hba1c?: number;
  diabetesHistory: {
    prediabetes: boolean;
    type2Diabetes: boolean;
    gestationalDiabetes: boolean;
    insulinResistanceDiagnosis: boolean;
  };
  diabetesMedications: string[];
  lipidAbnormalities: boolean;
  highBloodPressure: boolean;
  priorWeightLossMeds: string[];
  interestedInGlp1: boolean;
}

export interface MentalHealthData {
  anxietySymptoms: 'none' | 'mild' | 'moderate' | 'severe';
  depressionSymptoms: 'none' | 'mild' | 'moderate' | 'severe';
  sleepQuality: 'good' | 'fair' | 'poor' | 'very-poor';
  sleepDurationHours: number;
  sleepApneaSymptoms: boolean;
  stressLevel: 'low' | 'moderate' | 'high' | 'very-high';
  fatigueSeverity: 'none' | 'mild' | 'moderate' | 'severe';
}

export interface InflammatoryData {
  autoimmuneDiagnoses: string[];
  thyroidDisorder: boolean;
  thyroidType?: 'hypothyroid' | 'hyperthyroid' | 'hashimotos' | 'graves' | 'other';
  gutSymptoms: {
    ibs: boolean;
    bloating: boolean;
    constipation: boolean;
    diarrhea: boolean;
    foodSensitivities: string[];
  };
  chronicFatigue: boolean;
  jointPain: boolean;
  frequentHeadaches: boolean;
  crpTested: boolean;
  crpValue?: number;
  vitaminDTested: boolean;
  vitaminDValue?: number;
}

export interface FamilyHistoryData {
  pcosInFamily: boolean;
  pcosRelatives: string[];
  diabetesInFamily: boolean;
  obesityInFamily: boolean;
  cardiovascularDiseaseInFamily: boolean;
  thyroidDisordersInFamily: boolean;
  infertilityInFamily: boolean;
}

export interface LifestyleData {
  dietPattern: 'high-sugar' | 'processed' | 'balanced' | 'low-carb' | 'vegetarian' | 'other';
  exerciseFrequency: 'sedentary' | 'light' | 'moderate' | 'vigorous';
  exerciseType?: string[];
  smokingStatus: 'never' | 'former' | 'current';
  alcoholUse: 'none' | 'occasional' | 'moderate' | 'heavy';
  endocrineDisruptorAwareness: boolean;
}

export interface GLP1ScreeningData {
  giDiseaseHistory: boolean;
  pancreatitisHistory: boolean;
  severeGiCondition: boolean;
  thyroidCancerFamilyHistory: boolean;
  mtcMen2History: boolean;
  medicationsAffectedByGastricEmptying: string[];
  confirmNotPregnant: boolean;
  confirmNotBreastfeeding: boolean;
}

// ============================================================================
// LAB DATA TYPES
// ============================================================================

export interface ParsedLabValue {
  name: string;
  value: number;
  unit: string;
  referenceRange?: {
    low: number;
    high: number;
  };
  isAbnormal?: boolean;
  confidence: number;
}

export interface LabUploadResult {
  success: boolean;
  parsedValues: ParsedLabValue[];
  rawText?: string;
  errors?: string[];
  warnings?: string[];
}

export interface NimCoreLabPanel {
  estradiol?: ParsedLabValue;
  cortisol?: ParsedLabValue;
  totalTestosterone?: ParsedLabValue;
  progesterone?: ParsedLabValue;
  tsh?: ParsedLabValue;
  dhea?: ParsedLabValue;
  // Extended markers
  freeTestosterone?: ParsedLabValue;
  lh?: ParsedLabValue;
  fsh?: ParsedLabValue;
  lhFshRatio?: number;
  amh?: ParsedLabValue;
  shbg?: ParsedLabValue;
  fastingGlucose?: ParsedLabValue;
  fastingInsulin?: ParsedLabValue;
  homaIr?: number;
  totalCholesterol?: ParsedLabValue;
  ldl?: ParsedLabValue;
  hdl?: ParsedLabValue;
  triglycerides?: ParsedLabValue;
  vitaminD?: ParsedLabValue;
  crp?: ParsedLabValue;
}

// ============================================================================
// COMPLETE QUIZ DATA
// ============================================================================

export interface PCOSQuizData {
  demographics: DemographicsData;
  menstrual: MenstrualData;
  hyperandrogenic: HyperandrogenicData;
  metabolic: MetabolicData;
  mentalHealth: MentalHealthData;
  inflammatory: InflammatoryData;
  familyHistory: FamilyHistoryData;
  lifestyle: LifestyleData;
  glp1Screening: GLP1ScreeningData;
  labData?: NimCoreLabPanel;
  uploadedLabFile?: boolean;
}

// ============================================================================
// SCORING TYPES
// ============================================================================

export interface DomainScore {
  score: number;
  maxScore: number;
  percentage: number;
  factors: string[];
}

export interface PCOSScores {
  ovulatoryDysfunction: DomainScore;
  hyperandrogenism: DomainScore;
  polycysticOvaries: DomainScore;
  insulinResistance: DomainScore;
  inflammation: DomainScore;
  adrenalFactors: DomainScore;
  postPillFactors: DomainScore;
  mentalHealthRisk: DomainScore;
  metabolicRisk: DomainScore;
  glp1Suitability: DomainScore;
}

export interface PCOSAssessmentResult {
  // Primary classification
  likelyPhenotype: PCOSPhenotype;
  phenotypeConfidence: number;
  phenotypeExplanation: string;
  
  // Endotype
  likelyEndotype: PCOSEndotype;
  endotypeConfidence: number;
  endotypeExplanation: string;
  
  // Secondary phenotypes if mixed presentation
  secondaryPhenotypes?: PCOSPhenotype[];
  
  // Domain scores
  scores: PCOSScores;
  
  // Risk flags
  riskFlags: {
    highInsulinResistance: boolean;
    significantInflammation: boolean;
    mentalHealthConcern: boolean;
    metabolicSyndromeRisk: boolean;
    fertilityConcern: boolean;
    possibleTumor: boolean; // Very high androgens
  };
  
  // Recommendations
  recommendations: {
    lifestyle: string[];
    testing: string[];
    treatments: string[];
    specialists: string[];
    resources: string[];
  };
  
  // GLP-1 assessment
  glp1Assessment: {
    suitable: boolean;
    contraindicated: boolean;
    reasons: string[];
    cautions: string[];
    recommendedProtocol?: 'none' | 'microdose' | 'standard';
  };
  
  // NimCore recommendation
  nimCoreRecommendation: {
    recommended: boolean;
    markersToTrack: string[];
    rationale: string;
  };
}

// ============================================================================
// UI STATE TYPES
// ============================================================================

export interface QuizProgress {
  currentSection: number;
  totalSections: number;
  completedSections: number[];
  isComplete: boolean;
}

export type QuizSection = 
  | 'demographics'
  | 'menstrual'
  | 'hyperandrogenic'
  | 'metabolic'
  | 'mentalHealth'
  | 'inflammatory'
  | 'familyHistory'
  | 'lifestyle'
  | 'labUpload'
  | 'glp1Screening';

export const QUIZ_SECTIONS: { id: QuizSection; title: string; description: string; whyWeAsk: string }[] = [
  { 
    id: 'demographics', 
    title: 'About You', 
    description: 'Basic health information',
    whyWeAsk: 'Your age, weight, and current health status help us calculate metabolic markers and tailor recommendations to your life stage.'
  },
  { 
    id: 'menstrual', 
    title: 'Menstrual Health', 
    description: 'Cycle and reproductive history',
    whyWeAsk: 'Irregular cycles are a core diagnostic criterion for PCOS. Understanding your patterns helps identify ovulatory dysfunction.'
  },
  { 
    id: 'hyperandrogenic', 
    title: 'Hormonal Symptoms', 
    description: 'Signs of androgen excess',
    whyWeAsk: 'Symptoms like acne, excess hair, and hair thinning indicate androgen levels—key to determining your PCOS phenotype.'
  },
  { 
    id: 'metabolic', 
    title: 'Metabolic Health', 
    description: 'Weight, blood sugar, and metabolism',
    whyWeAsk: 'Insulin resistance drives most PCOS cases. These questions help identify if metabolic factors are contributing to your symptoms.'
  },
  { 
    id: 'mentalHealth', 
    title: 'Mental Wellness', 
    description: 'Sleep, stress, and mood',
    whyWeAsk: 'PCOS significantly impacts mental health. The 2023 guidelines emphasize screening for anxiety, depression, and sleep disorders.'
  },
  { 
    id: 'inflammatory', 
    title: 'Inflammation', 
    description: 'Autoimmune and gut health',
    whyWeAsk: 'Chronic inflammation can drive PCOS independently of insulin resistance. Gut health and immune function play important roles.'
  },
  { 
    id: 'familyHistory', 
    title: 'Family History', 
    description: 'Hereditary factors',
    whyWeAsk: 'PCOS has strong genetic components. Family patterns of diabetes, PCOS, and thyroid issues inform your risk profile.'
  },
  { 
    id: 'lifestyle', 
    title: 'Lifestyle', 
    description: 'Diet, exercise, and environment',
    whyWeAsk: 'Lifestyle factors significantly impact PCOS management. Understanding your habits helps us provide actionable recommendations.'
  },
  { 
    id: 'labUpload', 
    title: 'Lab Results', 
    description: 'Upload your test results (optional)',
    whyWeAsk: 'Lab data dramatically improves accuracy. We can analyze hormone levels, metabolic markers, and inflammatory indicators for precise phenotyping.'
  },
  { 
    id: 'glp1Screening', 
    title: 'Treatment Screening', 
    description: 'GLP-1 therapy eligibility',
    whyWeAsk: 'GLP-1 agonists show promise for PCOS. These questions ensure you don\'t have contraindications and help determine the right protocol for you.'
  },
];
